import { Controller } from '@hotwired/stimulus'
import WordCloud from 'wordcloud'
import { turboFetch } from '../util/common'

const MIN_FONT_SIZE = 14
const MAX_FONT_SIZE = 44
const GRID_SIZE = 12
const COLOR_CLASS_COUNT = 8

// Connects to data-controller="topics-cloud"
export default class extends Controller {
  static targets = ['cloud']
  static values = {
    topics: Array,
    colorful: { type: Boolean, default: false }
  }

  connect() {
    if (!this.hasCloudTarget) return

    this.colorClassByWord = new Map()
    this.lastWidth = 0
    this.lastHeight = 0
    this.draw()

    this.resizeObserver = new ResizeObserver(() => this.redrawIfSizeChanged())
    this.resizeObserver.observe(this.cloudTarget)
  }

  disconnect() {
    this.resizeObserver?.disconnect()
    WordCloud.stop()
  }

  redrawIfSizeChanged() {
    const cloud = this.cloudTarget
    if (cloud.offsetWidth === this.lastWidth && cloud.offsetHeight === this.lastHeight) return

    this.draw()
  }

  draw() {
    const cloud = this.cloudTarget
    if (cloud.offsetWidth === 0 || cloud.offsetHeight === 0) return

    this.lastWidth = cloud.offsetWidth
    this.lastHeight = cloud.offsetHeight

    WordCloud(cloud, {
      list: this.wordList(),
      fontFamily: '"Work Sans", sans-serif',
      fontWeight: '500',
      color: null,
      classes: this.colorfulValue ? word => this.colorClassFor(word) : null,
      weightFactor: 1,
      rotateRatio: 0,
      gridSize: GRID_SIZE,
      shape: 'circle',
      drawOutOfBound: false,
      shrinkToFit: false,
      backgroundColor: 'transparent',
      click: this.onClick.bind(this)
    })
  }

  onClick(item) {
    const url = item?.[2]
    if (url) turboFetch(url, {})
  }

  colorClassFor(word) {
    if (!this.colorClassByWord.has(word)) {
      const index = Math.floor(Math.random() * COLOR_CLASS_COUNT)
      this.colorClassByWord.set(word, `topic-color-${index}`)
    }
    return this.colorClassByWord.get(word)
  }

  wordList() {
    const counts = this.topicsValue.map(topic => Number(topic.value))
    const minCount = Math.min(...counts)
    const maxCount = Math.max(...counts)

    return this.topicsValue.map(topic => [
      topic.name,
      this.fontSizeFor(Number(topic.value), minCount, maxCount),
      topic.url
    ])
  }

  fontSizeFor(count, minCount, maxCount) {
    if (minCount === maxCount) return Math.round((MIN_FONT_SIZE + MAX_FONT_SIZE) / 2)

    const ratio = (count - minCount) / (maxCount - minCount)
    return Math.round(MIN_FONT_SIZE + ratio * (MAX_FONT_SIZE - MIN_FONT_SIZE))
  }
}
